import { AppIconModel } from "client_legacy/AppIcon/common";
import {
	ReactElement,
	useContext,
	useState
} from "react";
import {
	AppColorKind,
	AppColorViewModel,
	SaveAppColorModel
} from "client/Api/Apps/common";
import { never } from "client_legacy/ndaw/errors";
import { IValueInputAttachable } from "client/Forms/attachable";
import { ChooseAppIconDialog } from "./ChooseAppIconDialog/ChooseAppIconDialog";
import { UniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { SelectIcon } from "./SelectIcon";

export type AppIconSelectorProps = {
	value: AppIconModel | nil;
	onChange?(value: AppIconModel): void;
	color: AppColorViewModel | nil;
	trackingId: string;
};

export const AppIconSelector = (props: AppIconSelectorProps): ReactElement => {
	const {
		value,
		onChange,
		color,
		trackingId
	} = props;

	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

	const { id } = useContext(UniqueInputIdContext);

	const clickHandler = (): void => {
		setIsDialogOpen(true);
	};

	const iconSelectedHandler = (icon: AppIconModel): void => {
		setIsDialogOpen(false);

		if(onChange) {
			onChange(icon);
		}
	};

	const closeDialogHandler = (): void => {
		setIsDialogOpen(false);
	};

	const isEditable: boolean = Boolean(onChange);

	return (
		<>
			<SelectIcon
				id={id}
				trackingId={trackingId}
				image={
					value
						? {
							imageUrl: value.url,
							renderType: value.renderType
						}
						: undefined
				}
				imageBackgroundColor={{ isSet: true, value: color }}
				onClick={isEditable ? clickHandler : undefined}
			/>

			{isDialogOpen && (
				<ChooseAppIconDialog
					onSelected={iconSelectedHandler}
					onClose={closeDialogHandler}
				/>
			)}
		</>
	);
};
AppIconSelector.displayName = "AppIconSelector";

export function convertColorToViewModel(
	colorSaveModel: SaveAppColorModel,
	inheritedColor: AppColorViewModel | null
): AppColorViewModel | null {
	switch (colorSaveModel.kind) {
		case AppColorKind.Header:
		case AppColorKind.Primary:
			return colorSaveModel;
		case AppColorKind.Inherit:
			return inheritedColor;
		case AppColorKind.Custom:
			return {
				kind: AppColorKind.Custom,
				value: colorSaveModel.value ?? "#FF0000"
			};
		default:
			never(colorSaveModel);
	}
}

export function attachToAppIconSelector(
	attachable: IValueInputAttachable<AppIconModel | null>,
	isReadOnly: boolean = false
): Pick<AppIconSelectorProps, "value" | "onChange" | "trackingId"> {

	const value: AppIconModel | nil = attachable.value;

	const changeHandler: ((value: AppIconModel) => void) | undefined = isReadOnly
		? undefined
		: (x: AppIconModel) => {
			attachable
				.reducer
				.setValue(x);

			attachable
				.validation
				.triggerInputFinished();
		};

	return {
		value,
		onChange: changeHandler,
		trackingId: attachable.reducer
			.path
	};
}
